import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  fetchInboxMessage,
  fetchInboxPage,
  fetchInboxSummary,
  fetchLatestDailyRun,
  ignoreInboxMessage,
  runInboxAction,
  startDailyDirectorRun,
  type DirectorRun,
  type InboxFilters,
  type InboxServerAction,
} from './inbox-api';

/** The navigation badge refreshes every 60 s and on window focus (like the notification bell). */
const SUMMARY_REFRESH_MS = 60_000;
/** How often a started director run is looked at until it has finished. */
export const RUN_POLL_MS = 2_500;

const INBOX_KEY = ['inbox'] as const;

export function useInboxSummaryQuery(enabled = true) {
  return useQuery({
    queryKey: [...INBOX_KEY, 'summary'],
    queryFn: fetchInboxSummary,
    refetchInterval: SUMMARY_REFRESH_MS,
    refetchOnWindowFocus: true,
    enabled,
    retry: false,
  });
}

export function useInboxListQuery(filters: InboxFilters) {
  return useInfiniteQuery({
    queryKey: [...INBOX_KEY, 'list', filters.status, [...filters.kinds], [...filters.severities]],
    queryFn: ({ pageParam }) => fetchInboxPage(pageParam, filters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });
}

export function useInboxMessageQuery(id: string | null) {
  return useQuery({
    queryKey: [...INBOX_KEY, 'message', id],
    queryFn: () => fetchInboxMessage(id!),
    enabled: id !== null,
    retry: false,
  });
}

/** Ignoring changes the list, the counts and the detail alike. */
export function useIgnoreInboxMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey: string }) => ignoreInboxMessage(id, idempotencyKey),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: INBOX_KEY }),
  });
}

/**
 * `accept`, `regenerate` and `create_campaign`: besides the inbox, the content
 * lists, the campaigns and the knowledge items change, so those reload too.
 */
export function useInboxAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, type, idempotencyKey }: { id: string; type: InboxServerAction; idempotencyKey: string }) => runInboxAction(id, type, idempotencyKey),
    onSuccess: () => {
      for (const key of [INBOX_KEY, ['content'], ['campaigns'], ['knowledge']]) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * The "run check now" action: starts the daily routine, then watches the latest
 * daily run until it is no longer `running` and reports it once through
 * `onFinished`. The list reloads then (the worker writes the messages).
 */
export function useDirectorRunStarter(onFinished: (run: DirectorRun) => void) {
  const queryClient = useQueryClient();
  const [startedId, setStartedId] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: startDailyDirectorRun,
    onSuccess: (run) => setStartedId(run.id),
  });
  const watch = useQuery({
    queryKey: [...INBOX_KEY, 'run', startedId],
    queryFn: fetchLatestDailyRun,
    enabled: startedId !== null,
    refetchInterval: RUN_POLL_MS,
    retry: false,
  });
  const run = watch.data;
  useEffect(() => {
    if (startedId === null || !run || run.id !== startedId || run.status === 'running') return;
    setStartedId(null);
    void queryClient.invalidateQueries({ queryKey: INBOX_KEY });
    onFinished(run);
  }, [run, startedId, queryClient, onFinished]);
  return { start, watching: startedId !== null };
}
